import { ArgumentNullException } from "./error";
import { Maybe } from "./util/maybe";
export const isCalendarDate = (val) => {
    return val && val.isCalendarDate;
};
export class CalendarDate {
    toDate() {
        return this.dayEnd();
    }
    constructor(year, month, day) {
        this.year = year;
        this.month = month;
        this.day = day;
    }
    get isCalendarDate() {
        return true;
    }
    toString() {
        return `${this.year.toString().padStart(4, "0")}-${this.month
            .toString()
            .padStart(2, "0")}-${this.day.toString().padStart(2, "0")}`;
    }
    serialize() {
        return this.toString();
    }
    toMonthAndYearString() {
        const myDate = new Date();
        myDate.setMonth(this.month - 1);
        myDate.setFullYear(this.year);
        return myDate.toLocaleDateString("en-US", {
            month: "long",
            year: "numeric",
        });
    }
    humanFriendly() {
        return `${this.month}/${this.day}/${this.year}`;
    }
    humanFriendlyPadded() {
        return `${this.month.toString().padStart(2, "0")}/${this.day
            .toString()
            .padStart(2, "0")}/${this.year}`;
    }
    monthBegin() {
        return new CalendarDate(this.year, this.month, 1);
    }
    nextMonth() {
        if (this.month >= 12) {
            return new CalendarDate(this.year + 1, 1, 1);
        }
        return new CalendarDate(this.year, this.month + 1, 1);
    }
    lessThan(other) {
        if (this.year < other.year)
            return true;
        if (this.year === other.year && this.month < other.month)
            return true;
        if (this.year === other.year &&
            this.month === other.month &&
            this.day < other.day)
            return true;
        return false;
    }
    equals(other) {
        return (this.year === other.year &&
            this.month === other.month &&
            this.day === other.day);
    }
    dayStart() {
        return new Date(this.year, this.month - 1, this.day, 0, 0, 0);
    }
    dayEnd() {
        return new Date(this.year, this.month - 1, this.day, 23, 59, 59);
    }
    withTime(hh = 0, mm = 0, ss = 0) {
        return new Date(this.year, this.month - 1, this.day, hh, mm, ss);
    }
    addDays(daysToAdd) {
        let date = this.dayStart();
        date.setDate(date.getDate() + daysToAdd);
        return CalendarDate.fromDate(date);
    }
    static fromDate(date) {
        if (!date) {
            throw new ArgumentNullException("date should not be null, but was.");
        }
        // parse from ISO string as a way to enforce UTC consistency
        return CalendarDate.parse(date.toISOString()).getOrElse(CalendarDate.epoch);
    }
    /**
     * Use when user's timezone is important, such as when needing start of day in user's timezone.
     */
    static fromLocalDate(date) {
        if (!date) {
            throw new ArgumentNullException("date should not be null, but was.");
        }
        if (!date.getFullYear || !date.getMonth || !date.getDate) {
            throw new ArgumentNullException("date should be a Date object, but was not.");
        }
        return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
    }
    static parse(val) {
        if (val === null || val === undefined || val === "") {
            return Maybe.none();
        }
        if (isCalendarDate(val)) {
            return Maybe.fromValue(val);
        }
        const str = val instanceof Date ? val.toISOString() : val;
        const [datePart] = str.split("T");
        if (datePart === null || datePart === undefined || datePart === "") {
            return Maybe.none();
        }
        if (datePart.match(/^\d+-\d+-\d+$/)) {
            const segments = datePart.split("-").map(x => parseInt(x, 0));
            return Maybe.fromValue(new CalendarDate(segments[0], segments[1], segments[2]));
        }
        if (datePart.match(/^\d+\/\d+\/\d+$/)) {
            const segments = datePart.split("/").map(x => parseInt(x, 0));
            return Maybe.fromValue(new CalendarDate(segments[2], segments[0], segments[1]));
        }
        if (datePart.match(/\d{1,2}\/\d{1,2}\/\d{4}/)) {
            const segments = datePart.split("/").map(x => parseInt(x, 0));
            return Maybe.fromValue(new CalendarDate(segments[2], segments[0], segments[1]));
        }
        return Maybe.none();
    }
    toMoment() {
        return new CalendarDate(this.year, this.month - 1, this.day);
    }
    static get epoch() {
        return new CalendarDate(1970, 1, 1);
    }
    static get future() {
        return new CalendarDate(2099, 12, 31);
    }
    static get today() {
        return CalendarDate.fromDate(new Date());
    }
    static get todayNoTime() {
        const now = new Date();
        return new CalendarDate(now.getFullYear(), now.getMonth() + 1, now.getDate());
    }
    static todayInTimezone(timezone) {
        const now = new Date();
        const dateString = now.toLocaleString("en-US", { timeZone: timezone });
        return CalendarDate.parse(dateString).getOrElse(CalendarDate.todayNoTime);
    }
    // expects a string with "YYYY-MM-DD" format
    static isRawDate(val) {
        if (typeof val !== "string" || !val.match(/^\d{4}-\d{2}-\d{2}$/)) {
            return false;
        }
        const [yearStr, monthStr, dayStr] = val.split("-");
        const year = parseInt(yearStr, 10);
        const month = parseInt(monthStr, 10);
        const day = parseInt(dayStr, 10);
        if (year < 1900 || year > 2099) {
            return false;
        }
        if (month < 1 || month > 12) {
            return false;
        }
        const daysInMonth = new Date(year, month, 0).getDate(); // Use 0 to get the last day of the previous month
        if (day < 1 || day > daysInMonth) {
            return false;
        }
        return true;
    }
}
export const CALENDAR_DATE_NO_YEAR_PLACEHOLDER = 1900;
//# sourceMappingURL=calendar-date.js.map